import type { Town } from "./towns";
import type { FAQ, ServiceKey } from "./geoServices";
import { permitOfficesBySlug } from "./permitOffices";

const serviceNoun: Record<ServiceKey, string> = {
  roofing: "roof replacement",
  siding: "siding replacement",
  gutters: "gutter installation",
};

const serviceVerb: Record<ServiceKey, string> = {
  roofing: "replace your roof",
  siding: "replace your siding",
  gutters: "install or replace gutters",
};

const hoaElement: Record<ServiceKey, string> = {
  roofing: "roof",
  siding: "siding",
  gutters: "gutters and downspouts",
};

const hoaRegulates: Record<ServiceKey, string> = {
  roofing:
    "HOAs commonly limit shingle color, material type, and the visible roof profile to keep the streetscape consistent.",
  siding:
    "HOAs commonly limit siding color, material, and texture, and sometimes trim and accent details, to keep the streetscape consistent.",
  gutters:
    "HOAs commonly limit gutter and downspout color and placement so they blend with the home and the streetscape.",
};

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Builds at least five permitting and HOA FAQs for a geo service page. When the
 * town has one or more official municipal permit pages on file, the first FAQ
 * carries them as reference links; otherwise the link is simply omitted.
 */
export function buildPermitHoaFaqs(town: Town, service: ServiceKey): FAQ[] {
  const offices = permitOfficesBySlug[town.slug] ?? [];
  const noun = serviceNoun[service];
  const verb = serviceVerb[service];
  const links = offices.map((o) => ({ label: `${o.name} permit information`, url: o.url }));

  let officeRef: string;
  if (offices.length === 1) {
    officeRef = `In ${town.name}, building permits are handled by ${offices[0].name}. You can review current requirements, forms, and fees on the official page linked below.`;
  } else if (offices.length > 1) {
    officeRef = `${town.name} addresses can fall under more than one municipality, so confirm which one covers your property. Permits here are handled by ${joinNames(
      offices.map((o) => o.name),
    )}. The official pages are linked below.`;
  } else {
    officeRef = `Confirm permit requirements with your local borough or township office before work begins.`;
  }

  const faqs: FAQ[] = [
    {
      question: `Do I need a permit to ${verb} in ${town.name}, PA?`,
      answer: `In most ${town.county} municipalities, a full ${noun} requires a building permit, while small repairs often do not. Requirements vary by municipality, so it is best to confirm before scheduling. ${officeRef} Graystone can prepare and submit the permit application as part of your project.`,
      links: links.length ? links : undefined,
    },
    {
      question: `Who is responsible for pulling the permit, me or my contractor?`,
      answer: `Your contractor should handle it. As a licensed Pennsylvania home improvement contractor, Graystone prepares the permit application, submits the required documents, and coordinates inspections so you do not have to navigate the municipal process yourself.`,
    },
    {
      question: `How long does it take to get a permit for ${noun} in ${town.name}?`,
      answer: `Timelines depend on the municipality and how busy the office is. Many local boroughs and townships issue residential building permits within a few business days to about two weeks. We submit paperwork early so permitting does not hold up your project schedule.`,
    },
    {
      question: `Will my ${noun} need a municipal inspection?`,
      answer: `Permitted exterior work is commonly subject to a municipal inspection, and some municipalities require both an application review and a final inspection. Graystone schedules and meets any required inspections as part of the job.`,
    },
    {
      question: `Do I need HOA approval before starting work in ${town.name}?`,
      answer: `If your neighborhood has a homeowners association, you most likely need written architectural approval before changing the color, material, or profile of your ${hoaElement[service]}. Submit your request to the HOA architectural review committee before work begins. We provide product specifications and color samples to support your application.`,
    },
    {
      question: `What do HOAs usually regulate for ${hoaElement[service]}?`,
      answer: `${hoaRegulates[service]} We help you choose compliant options and document them for the approval process.`,
    },
  ];

  return faqs;
}
